import type { ReactNode } from 'react';

import { Icon, type IconName } from './Icon.tsx';

/**
 * What a panel looks like when there is nothing in it.
 *
 * The reference panel's `state` block: one emblem — a soft tinted disc ringed by two halos that
 * fade outward — over a heading and a line. A single grey sentence stranded in a wide card reads
 * as a page that failed to load; the same sentence under a mark reads as a shelf that is empty on
 * purpose, and the two are indistinguishable to someone who has just arrived.
 *
 * The dashed ring is the one visual difference between "there is nothing here" and "something
 * went wrong": a dash is an outline waiting to be filled, and an error's ring stays solid because
 * nothing about it is pending.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
  tone = 'empty',
}: {
  icon: IconName;
  title: string;
  hint?: string;
  /** A way out, where there is one — "add the first product", "try again". */
  action?: ReactNode;
  tone?: 'empty' | 'error';
  /** Kept for callers that asked for the taller panel; the reference sizes it by its own padding. */
  tall?: boolean;
}) {
  return (
    <div className={`card state state--${tone}`}>
      <span className="state__icon">
        <Icon name={icon} size={26} />
      </span>
      <p className="state__title">{title}</p>
      {hint ? <p className="state__description">{hint}</p> : null}
      {action ? <div className="state__action">{action}</div> : null}
    </div>
  );
}
